import { Box, RadioSelectableCard, Spacer, Typography } from '@qbe/ads-core';
import React, { useEffect } from 'react';

import MonthlyPaymentDay from '~/components/MonthlyPaymentDay';
import { Option, useAppContent, useAppDispatch, useOptionSelect, usePayments } from '~/hooks';
import {
  updateBankAccountBsb,
  updateBankAccountName,
  updateBankAccountNumber,
  updatePaymentFrequency,
  updatePaymentMethod,
} from '~/slices/payments';
import { PaymentFrequencyOptions, PaymentMethodOptions } from '~/types/payments';
import { currencyFormatter } from '~/utils/currencyFormatter';

interface PaymentFrequencyProps {
  firstInstalment: number | undefined;
  futureInstalment: number | undefined;
  oneOffPremium: number | undefined;
  onTouch?: () => void;
}

const toValue = {
  [PaymentFrequencyOptions.Monthly]: 'Monthly',
  [PaymentFrequencyOptions.OneOff]: 'One-off',
};

const options: Option<string>[] = Object.values(PaymentFrequencyOptions).map((option) => ({
  id: option,
  value: toValue[option],
}));

const PaymentFrequency: React.FC<PaymentFrequencyProps> = ({
  firstInstalment,
  futureInstalment,
  oneOffPremium,
  onTouch,
}) => {
  const { getTextContent, getJSXContent } = useAppContent({ basePath: 'dsme.payments' });

  const dispatch = useAppDispatch();
  const { paymentFrequency } = usePayments();

  const { selectedIDs, onSelectID, setSelectedIDs } = useOptionSelect({
    options,
  });

  useEffect(() => {
    setSelectedIDs([paymentFrequency!]);
  }, [paymentFrequency, setSelectedIDs]);

  const onChange = (id: string) => {
    if (!selectedIDs.includes(id)) {
      onSelectID(id);
      dispatch(updatePaymentFrequency(id as PaymentFrequencyOptions));
    }
    if (id === PaymentFrequencyOptions.OneOff) {
      dispatch(updatePaymentMethod(PaymentMethodOptions.CreditCard));
      dispatch(updateBankAccountName(null));
      dispatch(updateBankAccountBsb(null));
      dispatch(updateBankAccountNumber(null));
    }
    onTouch && onTouch();
  };

  const radioList = options.map(({ id, value }) => ({
    id,
    value: id,
    label: value,
    customComponent: (
      <div>
        {currencyFormatter(
          id === PaymentFrequencyOptions.Monthly ? futureInstalment : oneOffPremium
        )}
      </div>
    ),
    'data-track': value,
    'data-track-value': 'capture',
  }));

  return (
    <Box textAlign="center" data-testid="payment-frequency">
      <Typography variant="subtitle2" component="h3" gutterBottom>
        {getTextContent('content.frequency.title')}
      </Typography>
      <Typography>{getJSXContent('content.frequency.description')}</Typography>

      <RadioSelectableCard
        title=""
        direction="vertical"
        name="paymentFrequency"
        radioList={radioList}
        onChange={(_id, value) => onChange(value.id)}
        defaultValue={paymentFrequency!}
      />

      <Spacer size="xl" />

      {paymentFrequency === PaymentFrequencyOptions.Monthly &&
        firstInstalment &&
        futureInstalment && (
          <>
            <MonthlyPaymentDay
              firstInstalment={firstInstalment}
              futureInstalment={futureInstalment}
            />
            <Spacer size="md" />
          </>
        )}
    </Box>
  );
};

export default PaymentFrequency;
